import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MFACodeInput } from './MFACodeInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { generateDeviceFingerprint, getDeviceName } from '@/utils/mfaHelpers';

interface MFAVerifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  factorId: string;
  onSuccess: () => void;
}

export const MFAVerifyDialog = ({ open, onOpenChange, factorId, onSuccess }: MFAVerifyDialogProps) => {
  const [trustDevice, setTrustDevice] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { toast } = useToast();

  const handleDeviceTrust = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const fingerprint = await generateDeviceFingerprint();
      const deviceName = getDeviceName();
      const trustedUntil = new Date();
      trustedUntil.setDate(trustedUntil.getDate() + 30);

      await supabase.from('mfa_devices').insert({
        user_id: user.id,
        device_fingerprint: fingerprint,
        device_name: deviceName,
        trusted_until: trustedUntil.toISOString()
      });
    }
  };

  const handleVerify = async (code: string) => {
    setVerifying(true);
    try {
      // Check if this is a backup code (8 characters, alphanumeric)
      if (code.length === 8 && /^[A-Z0-9]+$/.test(code)) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          toast({ variant: 'destructive', title: 'Fehler', description: 'Nicht angemeldet' });
          return;
        }

        const { data, error: verifyError } = await supabase.functions.invoke('verify-backup-code', {
          body: { code },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (verifyError || !data?.valid) {
          toast({ variant: 'destructive', title: 'Fehler', description: 'Ungültiger Backup-Code' });
          setVerifying(false);
          return;
        }

        toast({ title: 'Backup-Code akzeptiert' });
        
        if (trustDevice) {
          await handleDeviceTrust();
        }
        
        onSuccess();
        onOpenChange(false);
        setVerifying(false);
        return;
      }

      // Regular TOTP code
      const { data, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error) throw error;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: data.id,
        code
      });

      if (verifyError) throw verifyError;

      if (trustDevice) {
        await handleDeviceTrust();
      }

      toast({ title: 'Erfolgreich verifiziert' });
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Ungültiger Code'
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Zwei-Faktor-Authentifizierung</DialogTitle>
          <DialogDescription>
            Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App oder einen 8-stelligen Backup-Code ein
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <MFACodeInput onComplete={handleVerify} disabled={verifying} />
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="trust"
              checked={trustDevice}
              onCheckedChange={(checked) => setTrustDevice(checked as boolean)}
            />
            <Label htmlFor="trust" className="text-sm">
              Dieses Gerät für 30 Tage als vertrauenswürdig markieren
            </Label>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
